import FullScreenMap from 'components/FullScreenMap';
import { usePersonnel } from 'features/Responder/features/Personnel/contexts/PersonnelContext';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import {
  deriveCheckpointsFromTour,
  resolveTourStartLatLng,
  toLatLng,
} from 'features/Responder/features/Tours/utils';
import { focusMapOnGeometry } from 'features/Responder/utils/focusMapOnGeometry';
import { buildTourGeometryFocusKey } from 'features/Responder/utils/focusKeys';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Circle, Marker, Polygon } from 'react-leaflet';
import './css/TourMap.css';
// CheckpointOverlay import retained for reference.
// import CheckpointOverlay from './CheckpointOverlay';
import BaseMap from 'components/BaseMap/BaseMap';
import { resolveZonePolygon } from 'features/Responder/features/Tours/features/TourBuilder/utils/zoneRouteMath';
import CheckpointsLayer from './Components/CheckpointsLayer';
import MapClickHandler from './Components/MapClickHandler';
import PersonelLayer from './Components/PersonelLayer';
import RouteLayer from './Components/RouteLayer';
import TourToolsPanel from './Components/TourToolsPanel';
import { useCheckpointActions } from './hooks/useCheckpointActions';
import { useFitBounds } from './hooks/useFitBounds';
import { useRouteProjection } from './hooks/useRouteProjection';
import { useUserLocation } from './hooks/useUserLocation';
import { almostEqual, parseCenterLatLng } from './utils/centerMath';
import { getCumulativeEtaSeconds, getLegStartsMeters } from './utils/routeLegMetrics';
import round4 from './utils/round4';

/**
 * Interactive map that visualizes the responder's draft/active tour, route polyline,
 * and draggable checkpoints. Also handles on-map checkpoint creation and ETA updates.
 * Relies on TourContext for shared state. Optional extraLayers allow other overlays
 * (e.g., zones) to render inside the map container.
 * @param {object} props
 * @param {boolean} props.showZoneAnchors
 * @returns {JSX.Element}
 */
export default function TourMap({
  extraLayers = null,
  routeColor = null,
  editable = true,
  viewTour = null,
  focusViewTourOnChange = false,
  addZoneAt = null,
  onZonePlaced = null,
  setZonePlacementPreview = null,
  maxZoom = 23,
  showTourTools = true,
  setShowTourTools = () => {},
  showZoneAnchors = true,
  showPersonnel = true,
  showTourToolsPanel = true,
  toolsOverlay = null,
  disableLocationSync: disableLocationSyncProp = null,
}) {
  const {
    activeTourData,
    center,
    checkpoints,
    addCheckpoint,
    updateCheckpoint,
    selectedCheckpointId,
    setSelectedCheckpointId,
    mapTool,
    setMapTool,
    stops,
    routeData,
    routeData: contextRouteData,
    startLocation,
    setStartLocation,
    snapToRoads,
    travelMode,
    builderTourData,
  } = useTours();

  const { personnel } = usePersonnel();

  const [mapZoom, setMapZoom] = useState(10);
  const [, setCenterSyncTick] = useState(0);
  const mapRef = useRef(null);
  const suppressFocusNext = useRef(false);
  const suppressAutoFitNext = useRef(false);
  const [mapReadyTick, setMapReadyTick] = useState(0);
  const [holdLocationSyncAfterZone, setHoldLocationSyncAfterZone] = useState(false);
  const prevZonePlacementFlowRef = useRef(false);
  const [implicitLocationSyncConsumed, setImplicitLocationSyncConsumed] = useState(false);
  const lastFocusedViewTourKeyRef = useRef(null);

  const googleApiKey =
    (typeof import.meta !== 'undefined' && import.meta.env?.REACT_APP_GOOGLE_MAPS_API_KEY) ||
    (typeof import.meta !== 'undefined' && import.meta.env?.PUBLIC_GOOGLE_MAPS_API_KEY) ||
    (typeof process !== 'undefined' && process.env?.REACT_APP_GOOGLE_MAPS_API_KEY) ||
    (typeof window !== 'undefined' && window.REACT_APP_GOOGLE_MAPS_API_KEY) ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('GOOGLE_MAPS_API_KEY')) ||
    '';

  const { userPosition, hasGeolocated, setUserPosition, setHasGeolocated, effectiveCenter } =
    useUserLocation(center);

  const focusTour = viewTour ?? activeTourData;

  const snapEnabled = snapToRoads && travelMode !== 'INDOOR';
  const isZonePlacementFlow = mapTool?.mode === 'zone';
  const isAwaitingZoneShape =
    mapTool?.mode === 'zone' && !!mapTool?.awaitingShape && !mapTool?.shape;

  const derivedView = useMemo(() => {
    if (!viewTour) return { checkpoints: [], routeData: null };
    const { checkpoints: cps } = deriveCheckpointsFromTour(viewTour);
    const route = (() => {
      if (viewTour.encodedPolyline || viewTour.polyline || viewTour.legs) {
        return {
          polyline: viewTour.encodedPolyline || viewTour.polyline || null,
          legs: Array.isArray(viewTour.legs) ? viewTour.legs : [],
          totalDuration: viewTour.totalDuration ?? null,
          totalDistanceMeters: viewTour.totalDistanceMeters ?? null,
        };
      }
      return null;
    })();
    return { checkpoints: cps, routeData: route };
  }, [viewTour]);

  const renderCheckpoints =
    Array.isArray(checkpoints) && checkpoints.length ? checkpoints : derivedView.checkpoints;

  const renderRouteData = contextRouteData || derivedView.routeData || routeData;

  const tourStopLatLngs = useMemo(() => {
    const sourceStops = Array.isArray(focusTour?.stops)
      ? focusTour.stops
      : Array.isArray(focusTour?.checkpoints)
        ? focusTour.checkpoints
        : [];
    if (!Array.isArray(sourceStops)) return [];
    return sourceStops
      .map((s) => {
        if (Array.isArray(s?.coordinates)) return toLatLng(s.coordinates);
        return toLatLng(s);
      })
      .filter(Boolean);
  }, [focusTour?.stops, focusTour?.checkpoints]);

  const stopsSignature = useMemo(() => {
    if (!tourStopLatLngs.length) return '';
    return tourStopLatLngs.map((p) => `${round4(p.lat)},${round4(p.lng)}`).join('|');
  }, [tourStopLatLngs]);

  const cumulativeEtaSeconds = useMemo(() => {
    return getCumulativeEtaSeconds(renderRouteData);
  }, [renderRouteData]);

  const legStartsMeters = useMemo(() => {
    return getLegStartsMeters(renderRouteData);
  }, [renderRouteData]);

  const {
    routePoints,
    routeMetrics,
    totalDurationSeconds,
    metersPerDegreeLat,
    metersPerDegreeLng,
  } = useRouteProjection(renderRouteData, focusTour);

  const {
    startIcon,
    handleIcon,
    checkpointIcon,
    personnelOnlineIcon,
    personnelOfflineIcon,
    computeEtaFor,
    addCheckpointAt,
    addStartAt,
    snapToRoad,
  } = useCheckpointActions({
    routePoints,
    routeMetrics,
    totalDurationSeconds,
    cumulativeEtaSeconds,
    metersPerDegreeLat,
    metersPerDegreeLng,
    checkpoints,
    stops,
    addCheckpoint,
    updateCheckpoint,
    setSelectedCheckpointId,
    snapEnabled,
    googleApiKey,
    startLocation,
    setStartLocation,
    userPosition,
    setUserPosition,
    setHasGeolocated,
    setMapZoom,
    suppressFocusNext,
    suppressAutoFitNext,
    mapRef,
    markerColor: routeColor || '#2e7be7',
  });

  // Editing enabled whenever parent allows (map tool or builder).
  const allowEditing = Boolean(editable);

  useFitBounds({
    mapRef,
    routePoints,
    tripStopLatLngs: tourStopLatLngs,
    stopsSignature,
    mapReadyKey: mapReadyTick,
    suppressNextFitRef: suppressAutoFitNext,
  });

  const findStopLatLng = (id) => {
    const match =
      stops.find((s) => `${s.id}` === `${id}`) ||
      activeTourData?.stops?.find?.((s) => `${s.id}` === `${id}`);
    if (!match) return null;
    return toLatLng(match) || toLatLng(match.coordinates);
  };

  const selectedCheckpointKey = useMemo(() => {
    if (selectedCheckpointId === 'start') {
      const lat = Number(startLocation?.lat);
      const lng = Number(startLocation?.lng);
      return Number.isFinite(lat) && Number.isFinite(lng) ? `start:${lat}:${lng}` : '';
    }
    if (typeof selectedCheckpointId === 'string' && selectedCheckpointId.startsWith('stop-')) {
      const stopId = selectedCheckpointId.replace('stop-', '');
      const ll = findStopLatLng(stopId);
      return ll ? `stop:${stopId}:${ll.lat}:${ll.lng}` : '';
    }
    const cp = checkpoints.find((c) => c.id === selectedCheckpointId);
    if (!cp || cp.lat == null || cp.lng == null) return '';
    return `${cp.id}:${cp.lat}:${cp.lng}`;
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkpoints,
    selectedCheckpointId,
    stops,
    activeTourData,
    startLocation?.lat,
    startLocation?.lng,
  ]);

  // Focus a checkpoint or stop on the map when requested
  useEffect(() => {
    if (!mapRef.current || !selectedCheckpointId) return;
    if (suppressFocusNext.current) {
      suppressFocusNext.current = false;
      return;
    }
    let target = null;
    if (selectedCheckpointId === 'start') {
      const lat = Number(startLocation?.lat);
      const lng = Number(startLocation?.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        target = { lat, lng };
      }
    } else if (
      typeof selectedCheckpointId === 'string' &&
      selectedCheckpointId.startsWith('stop-')
    ) {
      const stopId = selectedCheckpointId.replace('stop-', '');
      target = findStopLatLng(stopId);
    } else {
      target = checkpoints.find((c) => c.id === selectedCheckpointId);
      if (target && target.lat != null && target.lng != null) {
        target = { lat: target.lat, lng: target.lng };
      }
    }
    if (!target || target.lat == null || target.lng == null) return;
    try {
      const currentZoom = mapRef.current.getZoom();
      mapRef.current.setView([target.lat, target.lng], Math.min(Math.max(currentZoom, 15), 17), {
        animate: true,
      });
    } catch (err) {
      console.warn('center on checkpoint failed', err);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCheckpointId, selectedCheckpointKey, startLocation?.lat, startLocation?.lng]);

  const mapCenterProp = effectiveCenter;
  const stableCenterRef = useRef(mapCenterProp);
  const lastCenterZoomSyncKeyRef = useRef(null);

  const centerLatLng = useMemo(() => parseCenterLatLng(center), [center]);
  const routeStart = tourStopLatLngs.length ? tourStopLatLngs[0] : null;
  const centerMatchesRouteStart =
    !centerLatLng ||
    (routeStart &&
      almostEqual(centerLatLng.lat, routeStart.lat) &&
      almostEqual(centerLatLng.lng, routeStart.lng));
  const hasFitGeometry = routePoints.length > 1 || tourStopLatLngs.length > 1;
  const hasExplicitCenter = Boolean(centerLatLng);
  const disableImplicitLocationSync = !hasExplicitCenter && implicitLocationSyncConsumed;
  const disableLocationSync =
    typeof disableLocationSyncProp === 'boolean'
      ? disableLocationSyncProp
      : hasFitGeometry && centerMatchesRouteStart;
  const effectiveDisableLocationSync =
    disableLocationSync ||
    isZonePlacementFlow ||
    holdLocationSyncAfterZone ||
    disableImplicitLocationSync;

  useEffect(() => {
    if (!hasExplicitCenter && hasGeolocated) {
      setImplicitLocationSyncConsumed(true);
    }
  }, [hasExplicitCenter, hasGeolocated]);

  useLayoutEffect(() => {
    const wasZonePlacementFlow = prevZonePlacementFlowRef.current;
    prevZonePlacementFlowRef.current = isZonePlacementFlow;
    if (!wasZonePlacementFlow || isZonePlacementFlow) return;

    const liveZoom = Number(mapRef.current?.getZoom?.());
    if (Number.isFinite(liveZoom)) {
      setMapZoom((prev) => (prev === liveZoom ? prev : liveZoom));
    }
    setHoldLocationSyncAfterZone(true);
  }, [isZonePlacementFlow]);

  useEffect(() => {
    if (!holdLocationSyncAfterZone) return undefined;
    const release = () => {
      setHoldLocationSyncAfterZone(false);
    };
    if (typeof window?.requestAnimationFrame === 'function') {
      const rafId = window.requestAnimationFrame(release);
      return () => window.cancelAnimationFrame(rafId);
    }
    const timeoutId = setTimeout(release, 0);
    return () => clearTimeout(timeoutId);
  }, [holdLocationSyncAfterZone]);

  // Allow callers to pass an optional zoom as [lat, lng, zoom]
  useEffect(() => {
    if (!center) return;
    const parts = Array.isArray(center)
      ? center
      : [center.lat ?? center.latitude, center.lng ?? center.longitude, center.zoom];
    const zoomMaybe = Number(parts?.[2]);
    if (Number.isFinite(zoomMaybe)) {
      const clamped = Math.min(Math.max(zoomMaybe, 10), 18);
      const key = `${Number(parts?.[0])}:${Number(parts?.[1])}:${clamped}`;
      if (key === lastCenterZoomSyncKeyRef.current) return;
      lastCenterZoomSyncKeyRef.current = key;
      if (effectiveDisableLocationSync) return;
      setMapZoom(clamped);
    }
  }, [center, effectiveDisableLocationSync]);

  useEffect(() => {
    if (!effectiveDisableLocationSync) {
      stableCenterRef.current = mapCenterProp;
      return;
    }
    if (!stableCenterRef.current) {
      stableCenterRef.current = mapCenterProp;
    }
  }, [effectiveDisableLocationSync, mapCenterProp]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.on) return undefined;
    const syncZoom = () => {
      const nextZoom = Number(map.getZoom?.());
      if (!Number.isFinite(nextZoom)) return;
      setMapZoom((prev) => (almostEqual(prev, nextZoom) ? prev : nextZoom));
    };
    syncZoom();
    map.on('zoomend', syncZoom);
    return () => {
      map.off('zoomend', syncZoom);
    };
  }, [mapReadyTick]);

  useEffect(() => {
    if (!effectiveDisableLocationSync) return undefined;
    const map = mapRef.current;
    if (!map?.on) return undefined;
    const updateCenter = () => {
      const next = map.getCenter?.();
      if (!next) return;
      const nextCenter = [Number(next.lat), Number(next.lng)];
      const prev = stableCenterRef.current;
      if (!prev || Number(prev[0]) !== nextCenter[0] || Number(prev[1]) !== nextCenter[1]) {
        stableCenterRef.current = nextCenter;
        setCenterSyncTick((value) => value + 1);
      }
    };
    map.on('moveend', updateCenter);
    return () => {
      map.off('moveend', updateCenter);
    };
  }, [effectiveDisableLocationSync, mapReadyTick]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.on) return undefined;
    const syncZoom = () => {
      const nextZoom = Number(map.getZoom?.());
      if (!Number.isFinite(nextZoom)) return;
      setMapZoom((prev) => (prev === nextZoom ? prev : nextZoom));
    };
    syncZoom();
    map.on('zoomend', syncZoom);
    return () => {
      map.off('zoomend', syncZoom);
    };
  }, [effectiveDisableLocationSync, isZonePlacementFlow, mapReadyTick]);

  const viewStartLatLng = useMemo(() => resolveTourStartLatLng(viewTour), [viewTour]);

  const viewTourFocusKey = useMemo(
    () => buildTourGeometryFocusKey({ viewTour, routePoints, stopsSignature }),
    [viewTour, routePoints, stopsSignature],
  );

  useEffect(() => {
    if (!focusViewTourOnChange || !viewTourFocusKey) return;
    if (lastFocusedViewTourKeyRef.current === viewTourFocusKey) return;
    const map = mapRef.current;
    if (!map) return;
    lastFocusedViewTourKeyRef.current = viewTourFocusKey;
    try {
      if (routePoints.length > 1) {
        focusMapOnGeometry(map, routePoints, {
          padding: [80, 80],
          maxZoom: 17,
        });
        return;
      }
      if (tourStopLatLngs.length > 1) {
        focusMapOnGeometry(map, tourStopLatLngs, {
          padding: [80, 80],
          maxZoom: 17,
        });
        return;
      }
      const focusPoint = tourStopLatLngs[0] || viewStartLatLng || null;
      if (focusPoint) {
        focusMapOnGeometry(map, [focusPoint], {
          padding: [80, 80],
          maxZoom: 17,
          singlePointMinZoom: 15,
          singlePointMaxZoom: 17,
          animate: true,
        });
      }
    } catch (error) {
      console.warn('[TourMap] Failed to focus view tour', error);
    }
  }, [
    focusViewTourOnChange,
    routePoints,
    tourStopLatLngs,
    viewStartLatLng,
    viewTourFocusKey,
  ]);

  const resolvedMapCenter = effectiveDisableLocationSync ? stableCenterRef.current : mapCenterProp;

  const startLatLng = useMemo(() => {
    const rawLat = startLocation?.lat;
    const rawLng = startLocation?.lng;
    const hasExplicit =
      rawLat !== '' &&
      rawLng !== '' &&
      rawLat !== null &&
      rawLng !== null &&
      rawLat !== undefined &&
      rawLng !== undefined;
    if (hasExplicit) {
      const lat = Number(rawLat);
      const lng = Number(rawLng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    }
    return viewStartLatLng;
  }, [startLocation?.lat, startLocation?.lng, viewStartLatLng]);

  const startZonePoints = useMemo(() => {
    const geometry = startLocation?.geometry || {};
    const zoneId = startLocation?.zoneId ?? geometry?.zoneId ?? null;
    const type = (geometry?.type || geometry?.kind || '').toString().toLowerCase();
    const isZone = geometry?.isZone || type === 'zone' || type === 'polygon' || zoneId != null;
    if (!isZone) return [];
    const points = resolveZonePolygon(startLocation);
    return Array.isArray(points) && points.length >= 3 ? points : [];
  }, [startLocation]);

  return (
    <div
      className={`position-relative w-100 h-100 ${isAwaitingZoneShape ? 'zone-shape-guided' : ''}`}
    >
      <BaseMap
        mapCenter={resolvedMapCenter}
        zoom={mapZoom}
        maxZoom={maxZoom}
        mapRef={mapRef}
        scrollWheelZoom={true}
        disableLocationSync={effectiveDisableLocationSync}
        preserveLocationZoom={true}
        onMapReady={() => setMapReadyTick((prev) => prev + 1)}
      >
        <MapClickHandler
          mapTool={mapTool}
          addCheckpointAt={(latlng, geometryType) =>
            addCheckpointAt(latlng, geometryType, legStartsMeters)
          }
          addStartAt={addStartAt}
          addZoneAt={addZoneAt}
          onZonePlaced={onZonePlaced}
          setMapTool={setMapTool}
          setZonePlacementPreview={setZonePlacementPreview}
          legStartsMeters={legStartsMeters}
          editable={allowEditing}
        />
        <RouteLayer activeTourData={activeTourData} />
        <CheckpointsLayer
          checkpoints={renderCheckpoints}
          selectedCheckpointId={selectedCheckpointId}
          setSelectedCheckpointId={setSelectedCheckpointId}
          handleIcon={handleIcon}
          checkpointIcon={checkpointIcon}
          metersPerDegreeLat={metersPerDegreeLat}
          metersPerDegreeLng={metersPerDegreeLng}
          snapToRoad={snapToRoad}
          computeEtaFor={(latlng) => computeEtaFor(latlng, legStartsMeters)}
          updateCheckpoint={updateCheckpoint}
          suppressFocusNext={suppressFocusNext}
          legStartsMeters={legStartsMeters}
          editable={allowEditing}
          showZoneAnchors={showZoneAnchors}
        />
        {showPersonnel && (
          <PersonelLayer
            personnel={personnel}
            onlineIcon={personnelOnlineIcon}
            offlineIcon={personnelOfflineIcon}
          />
        )}
        {extraLayers}
        {startZonePoints.length >= 3 && (
          <Polygon
            positions={startZonePoints}
            pathOptions={{
              color: '#d9534f',
              fillColor: '#d9534f',
              fillOpacity: 0.15,
              weight: 2,
            }}
            interactive={false}
          />
        )}
        {startLatLng && (
          <Marker position={startLatLng} icon={startIcon} interactive={allowEditing} />
        )}
        {/* {
					activeTourData?.nodes?.map((node, idx) => {
						const latlng = toLatLng({
							lat: node.coords[0],
							lng: node.coords[1],
						});
						if (!latlng) return null;
						return (
							<Circle
								key={`node-${idx}`}
								center={latlng}
								radius={idx === 0 ? node.radius * 1: node.radius}
								pathOptions={{ color: idx === 0 ? 'green' : 'blue' }}
							/>
						);
					}
					)
				} */}
      </BaseMap>
      {isAwaitingZoneShape && <div className="zone-shape-guided-mask" />}
      {isZonePlacementFlow && (
        <div className="zone-shape-guided-instruction">
          {isAwaitingZoneShape ? (
            <>
              Pick a zone shape in Zone Tools. Press <kbd>Esc</kbd> to cancel.
            </>
          ) : (
            <>
              Click on the map to place the zone. Press <kbd>Esc</kbd> to cancel.
            </>
          )}
        </div>
      )}
      {toolsOverlay}

      {showTourTools && showTourToolsPanel && (
        <TourToolsPanel
          mapTool={mapTool}
          setMapTool={setMapTool}
          showTourTools={showTourTools}
          setShowTourTools={setShowTourTools}
        />
      )}

      {/* For possible future use: repositioned checkpoint editing onto cards, keeping overlay commented out. */}
      {/* <CheckpointOverlay
        checkpoint={
          checkpoints.find((cp) => cp.id === selectedCheckpointId) || null
        }
        etaSeconds={
          checkpoints.find((cp) => cp.id === selectedCheckpointId)
            ?.etaSeconds ?? null
        }
        onUpdate={(updates) => {
          if (!selectedCheckpointId) return;
          updateCheckpoint(selectedCheckpointId, updates);
        }}
        onClose={() => setSelectedCheckpointId(null)}
        onDelete={() => {
          if (!selectedCheckpointId) return;
          removeCheckpoint(selectedCheckpointId);
        }}
      /> */}
    </div>
  );
}


